/**
 * Import d'un export **Psono** JSON (« Autres > Exporter » de Psono, gestionnaire
 * allemand auto-hebergeable : ses utilisateurs doivent pouvoir partir avec TOUT
 * leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier porte `items` (racine) et `folders` (chacun avec
 * ses `items` et ses `folders`, a toute profondeur). Chaque element a un `type`
 * (`website_password`, `application_password`, `totp`, `note`, `bookmark`,
 * `environment_variables`, `mail_gpg_own_key`, `credit_card`, `ssh_own_key`) et
 * des cles prefixees par ce type (`website_password_username`...). Toute cle
 * non reprise devient un champ personnalise, masque si son libelle designe un
 * secret (aucune perte).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import type { SshKeyMaterial } from './ssh-key-item.js';
import { normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asArray(value: Json): readonly Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

function objects(value: Json): Record<string, Json>[] {
  return asArray(value)
    .map(asObject)
    .filter((item): item is Record<string, Json> => item !== null);
}

/** Profondeur maximale de dossiers lue : un JSON n'a pas de cycle, mais la pile n'est pas infinie. */
const MAX_FOLDER_DEPTH = 64;

/** Metadonnees d'un element, sans valeur pour l'utilisateur. */
const IGNORED_KEYS = new Set(['id', 'type', 'name', 'create_date', 'write_date', 'urlfilter', 'autosubmit']);

/** Suffixes lus par la fiche, par type (apres le prefixe) : ils ne redeviennent pas des champs. */
const LOGIN_SUFFIXES = ['title', 'url', 'username', 'password', 'notes', 'auto_submit', 'totp_code', 'totp_period', 'totp_digits', 'totp_algorithm'];
const READ_SUFFIXES: Readonly<Record<string, readonly string[]>> = {
  website_password: LOGIN_SUFFIXES,
  application_password: LOGIN_SUFFIXES,
  bookmark: LOGIN_SUFFIXES,
  totp: ['title', 'notes', 'code', 'period', 'digits', 'algorithm'],
  note: ['title', 'notes'],
  environment_variables: ['title', 'notes', 'variables'],
  mail_gpg_own_key: ['title', 'notes', 'name', 'email', 'public', 'private'],
  credit_card: ['title', 'notes', 'name', 'number', 'cvc', 'pin', 'valid_through'],
  ssh_own_key: ['title', 'notes', 'public', 'private'],
};
/** Type inconnu : lu comme un identifiant s'il porte un mot de passe, sinon comme une note. */
const UNKNOWN_SUFFIXES = ['title', 'notes', 'username', 'password', 'url', 'totp_code', 'totp_period', 'totp_digits', 'totp_algorithm'];

/** Suffixes lus pour un type ; `undefined` si le type est inconnu (un `constructor` du JSON n'est pas un type). */
function readSuffixes(type: string): readonly string[] | undefined {
  return Object.hasOwn(READ_SUFFIXES, type) ? READ_SUFFIXES[type] : undefined;
}

/**
 * Graine TOTP Psono : la graine base32 et ses parametres sont des cles
 * separees. Des parametres non standard sont portes par une URI `otpauth://`,
 * que `normalizeTotpSecret` garde entiere (sinon le code calcule serait faux).
 */
function psonoTotp(code: string, period: string, digits: string, algorithm: string): { secret: string; extraNote: string } {
  if (code.length === 0 || /^otpauth:\/\//iu.test(code)) return normalizeTotpSecret(code);
  const params = new URLSearchParams();
  if (period.length > 0 && period !== '30') params.set('period', period);
  if (digits.length > 0 && digits !== '6') params.set('digits', digits);
  if (algorithm.length > 0 && algorithm.toUpperCase() !== 'SHA1') params.set('algorithm', algorithm.toUpperCase());
  if ([...params.keys()].length === 0) return normalizeTotpSecret(code);
  params.set('secret', code.replace(/[\s-]+/gu, '').toUpperCase());
  return normalizeTotpSecret(`otpauth://totp/Psono?${params.toString()}`);
}

/** Cles d'un element non reprises par la fiche -> champs personnalises (libelle sans le prefixe du type). */
function extraFields(item: Record<string, Json>, prefix: string, read: ReadonlySet<string>): EntryCustomField[] {
  const fields: EntryCustomField[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (IGNORED_KEYS.has(key)) continue;
    const label = key.startsWith(prefix) ? key.slice(prefix.length) : key;
    if (key.startsWith(prefix) && read.has(label)) continue;
    // `false` est la valeur par defaut d'un drapeau Psono : il n'apprend rien.
    if (value === false) continue;
    const text = Array.isArray(value) ? value.map(asString).filter(Boolean).join(', ') : asObject(value) !== null ? JSON.stringify(value) : asString(value);
    if (text.length > 0) fields.push(csvColumnField(label, text));
  }
  return fields;
}

/**
 * Instant (ms) ou le mot de passe a ete pose : Psono ne date que l'element
 * (`write_date`, ISO 8601), derniere modification = borne haute, jamais
 * `create_date`. Aucune date sans mot de passe ; illisible : l'import vaut pose.
 */
function psonoPasswordChangedAt(item: Record<string, Json>, password: string): { passwordChangedAt?: number } {
  const passwordChangedAt = password.length > 0 ? Date.parse(asString(item['write_date'])) : NaN;
  return Number.isFinite(passwordChangedAt) ? { passwordChangedAt } : {};
}

function withFields(customFields: readonly EntryCustomField[]): { customFields?: readonly EntryCustomField[] } {
  return customFields.length > 0 ? { customFields } : {};
}

/** Variables d'environnement : chaque `{ key, value }` devient un champ, toujours masque (jetons, cles d'API). */
function environmentFields(value: Json): EntryCustomField[] {
  return objects(value)
    .map((variable) => ({ name: asString(variable['key']), value: rawSecret(variable['value']), hidden: true }))
    .filter((field) => field.name.length > 0 || field.value.length > 0);
}

function parsePsonoItem(item: Record<string, Json>, folderPath: readonly string[]): ImportedEntry {
  const type = asString(item['type']);
  const prefix = `${type}_`;
  const get = (suffix: string): string => asString(item[prefix + suffix]);
  const name = get('title') || asString(item['name']);
  const totp =
    type === 'totp'
      ? psonoTotp(get('code'), get('period'), get('digits'), get('algorithm'))
      : psonoTotp(get('totp_code'), get('totp_period'), get('totp_digits'), get('totp_algorithm'));
  const customFields = extraFields(item, prefix, new Set(readSuffixes(type) ?? UNKNOWN_SUFFIXES));
  const base = {
    name: name || 'Sans nom',
    username: '',
    password: '',
    uris: [] as string[],
    notes: joinNotes([get('notes'), totp.extraNote]),
    totpSecret: totp.secret,
    favorite: false,
    ...(folderPath.length > 0 ? { folderPath } : {}),
  };

  if (type === 'website_password' || type === 'application_password' || type === 'bookmark' || type === 'totp') {
    const url = get('url');
    const username = get('username');
    const password = rawSecret(item[`${prefix}password`]);
    return {
      ...base,
      type: 'login',
      name: name || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      ...psonoPasswordChangedAt(item, password),
      ...withFields(customFields),
    };
  }
  if (type === 'credit_card') {
    const typed: Record<string, string> = {};
    const set = (key: string, value: string): void => {
      if (value.length > 0) typed[key] = value;
    };
    set('cardholder', get('name'));
    set('number', get('number'));
    set('cvv', get('cvc'));
    set('pin', get('pin'));
    set('expiry', get('valid_through'));
    return { ...base, type: 'card', name: name || 'Carte', fields: normalizeTypedFields('card', typed), ...withFields(customFields) };
  }
  if (type === 'ssh_own_key') {
    // La cle privee ne passe JAMAIS en notes (affichees en clair) : elle devient la paire de l'entree.
    const sshKey: SshKeyMaterial = { privateKey: rawSecret(item[`${prefix}private`]).trim(), publicKey: get('public') };
    return { ...base, type: 'ssh-key', name: name || 'Cle SSH', sshKey, ...withFields(customFields) };
  }
  if (type === 'environment_variables') {
    return { ...base, type: 'note', ...withFields([...environmentFields(item[`${prefix}variables`]), ...customFields]) };
  }
  if (type === 'mail_gpg_own_key') {
    const gpg: EntryCustomField[] = [];
    if (get('name').length > 0) gpg.push({ name: 'Nom', value: get('name'), hidden: false });
    if (get('email').length > 0) gpg.push({ name: 'E-mail', value: get('email'), hidden: false });
    if (get('public').length > 0) gpg.push({ name: 'Cle publique', value: get('public'), hidden: false });
    // La cle privee GPG est masquee comme un mot de passe.
    if (get('private').length > 0) gpg.push({ name: 'Cle privee', value: get('private'), hidden: true });
    return { ...base, type: 'note', ...withFields([...gpg, ...customFields]) };
  }
  // `note` et tout type inconnu : une note, ou un identifiant s'il porte un mot de passe.
  const password = rawSecret(item[`${prefix}password`]);
  if (readSuffixes(type) === undefined && password.length > 0) {
    return { ...base, type: 'login', username: get('username'), password, uris: get('url') ? [get('url')] : [], ...psonoPasswordChangedAt(item, password), ...withFields(customFields) };
  }
  return { ...base, type: 'note', ...withFields(customFields) };
}

function collect(container: Record<string, Json>, path: readonly string[], depth: number, out: ImportedEntry[]): void {
  for (const item of objects(container['items'])) {
    if (item['deleted'] === true) continue;
    out.push(parsePsonoItem(item, path));
  }
  if (depth >= MAX_FOLDER_DEPTH) return;
  for (const folder of objects(container['folders'])) {
    if (folder['deleted'] === true) continue;
    const name = asString(folder['name']);
    collect(folder, name.length > 0 ? [...path, name] : path, depth + 1, out);
  }
}

/** Un element Psono : `type` texte et au moins une cle prefixee par ce type. */
function isPsonoItem(item: Record<string, Json>): boolean {
  const type = item['type'];
  return typeof type === 'string' && type.length > 0 && Object.keys(item).some((key) => key.startsWith(`${type}_`));
}

function hasPsonoItem(container: Record<string, Json>, depth: number): boolean {
  if (objects(container['items']).some(isPsonoItem)) return true;
  return depth < MAX_FOLDER_DEPTH && objects(container['folders']).some((folder) => hasPsonoItem(folder, depth + 1));
}

/**
 * Un export Psono se reconnait a un element (racine ou dossier) dont le `type`
 * est un texte prefixant ses cles (`website_password_url`...) : un tableau
 * `items` seul ne suffit pas (Bitwarden en porte un, a `type` numerique).
 */
export function isPsonoExport(data: Json): boolean {
  const file = asObject(data);
  return file !== null && hasPsonoItem(file, 0);
}

/** Parse un export Psono JSON : elements de la racine puis de chaque dossier, arborescence gardee. */
export function parsePsonoExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  if (file === null) throw new Error('export Psono : structure inattendue');
  const entries: ImportedEntry[] = [];
  collect(file, [], 0, entries);
  return entries;
}
